import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreenOutlined';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import PauseCircleIcon from '@mui/icons-material/PauseCircleOutline';
import type { BrandAsset } from '../../services/editingApi';
import type { EditList } from '../../utils/editing/editList';
import { segmentPlacements, segmentPlayMs, sourceTimeAt } from '../../utils/editing/editList';
import type { PreviewCue } from './PreviewOverlay';
import { firstWordsIn, formatMs, moveSegmentTo, trimSegmentEdge, type TranscriptWord } from './editStudioModel';
import { nearestFrame, useThumbnails } from './useThumbnails';

/**
 * The timeline: a ruler to scrub and mark a stretch on, the kept stretches as
 * clips (frames of the source, the first words said, a muted or held mark), and
 * a track each for text, pictures, captions, spoken lines and music, at their
 * times. Click to select (and open its settings), drag a clip's ends to trim it,
 * drag a clip to reorder, drag a bar to move it or its ends to retime it.
 */

export type StudioSelection =
  | { kind: 'segment'; id: string }
  | { kind: 'text'; id: string }
  | { kind: 'overlay'; id: string }
  | { kind: 'voiceover'; id: string }
  | { kind: 'music' }
  | { kind: 'captions' }
  | null;

export interface MarkedRange {
  fromMs: number | null;
  toMs: number | null;
}

interface TimelineProps {
  list: EditList;
  outputMs: number;
  durationMs: number;
  playing: boolean;
  videoUrl: string | null;
  assets: BrandAsset[];
  words: TranscriptWord[];
  cues: PreviewCue[];
  selection: StudioSelection;
  marked: MarkedRange;
  onSeek: (outputMs: number) => void;
  onSelect: (selection: StudioSelection) => void;
  onMarked: (range: MarkedRange) => void;
  /** Live changes from dragging, coalesced into one undo step per drag by `key`. */
  onChange: (next: EditList, key: string) => void;
}

const LABEL_WIDTH = 74;
const ROW = { ruler: 20, video: 48, text: 20, overlay: 20, captions: 14, voice: 20, music: 20 };
const CLIP_COLOURS = ['#0F766E', '#B45309', '#1D4ED8', '#7C3AED', '#BE185D', '#15803D'];
const THUMB_WIDTH = 92;
const MAX_THUMBS = 80;

type Drag =
  | { type: 'scrub' }
  | { type: 'mark'; anchorMs: number }
  | { type: 'markEdge'; edge: 'from' | 'to' }
  | { type: 'trim'; id: string; edge: 'in' | 'out'; startX: number; inMs: number; outMs: number; speed: number; key: string }
  | { type: 'reorder'; id: string; startX: number; moved: boolean; overIndex: number }
  | { type: 'bar'; kind: 'text' | 'overlay' | 'voiceover' | 'music'; id: string; part: 'body' | 'from' | 'to'; startX: number; fromMs: number; toMs: number; key: string };

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

/** A tick spacing that leaves about 80 px between labels. */
function tickStep(pxPerSecond: number): number {
  const steps = [0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 1200];
  return steps.find((step) => step * pxPerSecond >= 80) || 1800;
}

const Timeline: React.FC<TimelineProps> = ({ list, outputMs, durationMs, playing, videoUrl, assets, words, cues, selection, marked, onSeek, onSelect, onMarked, onChange }) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [viewWidth, setViewWidth] = useState(600);
  const [zoom, setZoom] = useState(1);
  const [hover, setHover] = useState<{ x: number; ms: number } | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () => setViewWidth(Math.max(200, el.clientWidth));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const total = Math.max(1, durationMs);
  const fitPps = Math.max(0.5, (viewWidth - 8) / (total / 1000));
  const pps = fitPps * zoom;
  const contentWidth = Math.max(viewWidth - 8, (total / 1000) * pps);
  const xOf = (ms: number) => (ms / 1000) * pps;
  const msOf = (x: number) => clamp((x / pps) * 1000, 0, total);
  const placements = useMemo(() => segmentPlacements(list), [list]);
  const step = tickStep(pps);

  // Keep the playhead in view while playing.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !playing) return;
    const x = xOf(outputMs);
    if (x < el.scrollLeft + 20 || x > el.scrollLeft + el.clientWidth - 40) el.scrollLeft = Math.max(0, x - el.clientWidth / 3);
  });

  // Thumbnails: one per THUMB_WIDTH px of each clip, as many as can be useful.
  const wantedFrames = useMemo(() => {
    const times: number[] = [];
    placements.forEach(({ segment, startMs }) => {
      const width = xOf(segmentPlayMs(segment));
      const count = Math.max(1, Math.ceil(width / THUMB_WIDTH));
      for (let i = 0; i < count && times.length < MAX_THUMBS; i += 1) {
        const outAt = startMs + (i * THUMB_WIDTH / pps) * 1000;
        times.push(segment.inMs + (outAt - startMs) * segment.speed);
      }
    });
    return times;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placements, Math.round(pps * 10)]);
  const frames = useThumbnails(videoUrl, wantedFrames);

  const pointerX = (event: React.PointerEvent | PointerEvent): number => {
    const rect = contentRef.current?.getBoundingClientRect();
    return rect ? event.clientX - rect.left : 0;
  };

  // --- Pointer handling: one drag at a time, captured on the content box -------------------
  const begin = (event: React.PointerEvent, drag: Drag) => {
    event.stopPropagation();
    dragRef.current = drag;
    contentRef.current?.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent) => {
    const x = pointerX(event);
    const rect = contentRef.current?.getBoundingClientRect();
    const y = rect ? event.clientY - rect.top : 0;
    // The frame preview follows the pointer along the ruler only, so it never covers the clips being worked on.
    setHover(y <= ROW.ruler ? { x, ms: msOf(x) } : null);
    const drag = dragRef.current;
    if (!drag) return;
    const ms = msOf(x);
    if (drag.type === 'scrub') {
      onSeek(ms);
    } else if (drag.type === 'mark') {
      onMarked({ fromMs: Math.min(drag.anchorMs, ms), toMs: Math.max(drag.anchorMs, ms) });
    } else if (drag.type === 'markEdge') {
      if (marked.fromMs === null || marked.toMs === null) return;
      if (drag.edge === 'from') onMarked({ fromMs: Math.min(ms, marked.toMs - 40), toMs: marked.toMs });
      else onMarked({ fromMs: marked.fromMs, toMs: Math.max(ms, marked.fromMs + 40) });
    } else if (drag.type === 'trim') {
      const deltaMs = ((x - drag.startX) / pps) * 1000 * drag.speed;
      const sourceMs = drag.edge === 'in' ? drag.inMs + deltaMs : drag.outMs + deltaMs;
      onChange(trimSegmentEdge(list, drag.id, drag.edge, sourceMs), drag.key);
    } else if (drag.type === 'reorder') {
      if (!drag.moved && Math.abs(x - drag.startX) < 6) return;
      drag.moved = true;
      let index = placements.length - 1;
      for (let i = 0; i < placements.length; i += 1) {
        const mid = xOf((placements[i].startMs + placements[i].endMs) / 2);
        if (x < mid) { index = i; break; }
      }
      drag.overIndex = index;
      setDropIndex(index);
    } else if (drag.type === 'bar') {
      const deltaMs = ((x - drag.startX) / pps) * 1000;
      const length = drag.toMs - drag.fromMs;
      let fromMs = drag.fromMs;
      let toMs = drag.toMs;
      if (drag.part === 'body') {
        fromMs = clamp(Math.round(drag.fromMs + deltaMs), 0, Math.max(0, total - length));
        toMs = fromMs + length;
      } else if (drag.part === 'from') {
        fromMs = clamp(Math.round(drag.fromMs + deltaMs), 0, drag.toMs - 100);
      } else {
        toMs = clamp(Math.round(drag.toMs + deltaMs), drag.fromMs + 100, total);
      }
      if (drag.kind === 'text') {
        onChange({ ...list, texts: list.texts.map((t) => (t.id === drag.id ? { ...t, fromMs, toMs } : t)) }, drag.key);
      } else if (drag.kind === 'overlay') {
        onChange({ ...list, overlays: list.overlays.map((o) => (o.id === drag.id ? { ...o, fromMs: fromMs <= 0 && drag.part !== 'from' && o.fromMs === null ? null : fromMs, toMs: toMs >= total && drag.part !== 'to' && o.toMs === null ? null : toMs } : o)) }, drag.key);
      } else if (drag.kind === 'voiceover') {
        onChange({ ...list, voiceovers: list.voiceovers.map((v) => (v.id === drag.id ? { ...v, atMs: clamp(Math.round(drag.fromMs + deltaMs), 0, total - 1) } : v)) }, drag.key);
      } else if (drag.kind === 'music' && list.music) {
        onChange({ ...list, music: { ...list.music, startMs: clamp(Math.round(drag.fromMs + deltaMs), 0, total - 1) } }, drag.key);
      }
    }
  };

  const onPointerUp = (event: React.PointerEvent) => {
    const drag = dragRef.current;
    dragRef.current = null;
    contentRef.current?.releasePointerCapture?.(event.pointerId);
    if (drag && drag.type === 'reorder') {
      setDropIndex(null);
      if (drag.moved) onChange(moveSegmentTo(list, drag.id, drag.overIndex), `reorder-${Date.now()}`);
    }
  };

  // --- Pieces ---------------------------------------------------------------------------------
  const isSelected = (kind: string, id?: string) => Boolean(selection && selection.kind === kind && (!id || (selection as { id?: string }).id === id));
  const hasMark = marked.fromMs !== null && marked.toMs !== null && marked.toMs > marked.fromMs;
  const hoverSource = hover ? sourceTimeAt(list, hover.ms) : null;
  const hoverFrame = hoverSource !== null ? nearestFrame(frames, hoverSource) : null;

  const bar = (kind: 'text' | 'overlay' | 'voiceover' | 'music', id: string, fromMs: number, toMs: number, label: string, colour: string, resizable: boolean) => {
    const left = xOf(fromMs);
    const width = Math.max(6, xOf(toMs) - left);
    const selected = isSelected(kind, kind === 'music' ? undefined : id);
    const key = () => `bar-${kind}-${id}-${Date.now()}`;
    return (
      <Box
        key={`${kind}-${id}`}
        data-testid={`track-${kind}-${id}`}
        role="button"
        aria-label={`${label}, ${formatMs(fromMs)} to ${formatMs(toMs)}`}
        onPointerDown={(event: React.PointerEvent) => {
          onSelect(kind === 'music' ? { kind: 'music' } : ({ kind, id } as StudioSelection));
          begin(event, { type: 'bar', kind, id, part: 'body', startX: pointerX(event), fromMs, toMs, key: key() });
        }}
        sx={{
          position: 'absolute', left, width, top: 2, bottom: 2, borderRadius: '4px', bgcolor: colour, color: '#fff',
          fontSize: 11, lineHeight: '16px', px: 0.75, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
          cursor: 'grab', boxShadow: selected ? '0 0 0 2px #FFFFFF, 0 0 0 4px #2563EB' : 'none', touchAction: 'none', userSelect: 'none',
        }}
      >
        {label}
        {resizable ? (
          <>
            <Box onPointerDown={(event: React.PointerEvent) => { onSelect({ kind, id } as StudioSelection); begin(event, { type: 'bar', kind, id, part: 'from', startX: pointerX(event), fromMs, toMs, key: key() }); }} sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, cursor: 'ew-resize' }} />
            <Box onPointerDown={(event: React.PointerEvent) => { onSelect({ kind, id } as StudioSelection); begin(event, { type: 'bar', kind, id, part: 'to', startX: pointerX(event), fromMs, toMs, key: key() }); }} sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 6, cursor: 'ew-resize' }} />
          </>
        ) : null}
      </Box>
    );
  };

  const assetName = (id: string | null) => assets.find((asset) => asset.id === id)?.name || 'Picture';
  const voiceLength = (voiceoverId: string): number => {
    const voiceover = list.voiceovers.find((v) => v.id === voiceoverId);
    if (!voiceover) return 1000;
    const asset = voiceover.source.assetId ? assets.find((a) => a.id === voiceover.source.assetId) : null;
    if (asset?.durationMs) return asset.durationMs;
    return voiceover.source.kind === 'tts' ? Math.max(800, (voiceover.source.text.length / 15) * 1000) : 2000;
  };

  const rows: Array<{ key: keyof typeof ROW; label: string }> = [
    { key: 'ruler', label: '' },
    { key: 'video', label: 'Video' },
    { key: 'text', label: 'Text' },
    { key: 'overlay', label: 'Pictures' },
    { key: 'captions', label: 'Captions' },
    { key: 'voice', label: 'Voice' },
    { key: 'music', label: 'Music' },
  ];
  const tops: Record<string, number> = {};
  rows.reduce((top, row) => { tops[row.key] = top; return top + ROW[row.key]; }, 0);
  const height = rows.reduce((sum, row) => sum + ROW[row.key], 0);

  const ticks: number[] = [];
  for (let t = 0; t <= total / 1000 + 0.001; t += step) ticks.push(t);

  return (
    <Box sx={{ bgcolor: '#0B1220', color: '#E5E7EB', borderRadius: 2, px: 1, pt: 0.25, pb: 0.75, userSelect: 'none' }} data-testid="timeline">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" noWrap sx={{ color: '#9CA3AF', flex: 1, minWidth: 0 }} title="Drag a clip’s edge to trim it, a clip to reorder, a bar to move it or its ends to retime it. Shift-drag the ruler to mark a stretch.">
          {hasMark ? `Marked ${formatMs(marked.fromMs as number)} – ${formatMs(marked.toMs as number)} · ` : ''}Drag an edge to trim, a clip to reorder, a bar to move · Shift-drag the ruler to mark a stretch
        </Typography>
        <Tooltip title="Zoom out"><span><IconButton size="small" sx={{ color: 'inherit' }} disabled={zoom <= 1} onClick={() => setZoom((z) => Math.max(1, z / 1.6))} aria-label="Zoom out"><ZoomOutIcon fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Fit the whole edit"><IconButton size="small" sx={{ color: 'inherit' }} onClick={() => setZoom(1)} aria-label="Fit the whole edit"><FitScreenIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Zoom in"><span><IconButton size="small" sx={{ color: 'inherit' }} disabled={zoom >= 64} onClick={() => setZoom((z) => Math.min(64, z * 1.6))} aria-label="Zoom in"><ZoomInIcon fontSize="small" /></IconButton></span></Tooltip>
      </Box>
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ width: LABEL_WIDTH, flex: 'none' }}>
          {rows.map((row) => (
            <Box key={row.key} sx={{ height: ROW[row.key], fontSize: 11, color: '#9CA3AF', display: 'flex', alignItems: 'center' }}>{row.label}</Box>
          ))}
        </Box>
        <Box ref={scrollRef} sx={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', position: 'relative' }} onPointerLeave={() => setHover(null)}>
          <Box
            ref={contentRef}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            sx={{ position: 'relative', width: contentWidth, height, touchAction: 'none' }}
          >
            {/* Ruler */}
            <Box
              data-testid="timeline-ruler"
              onPointerDown={(event: React.PointerEvent) => {
                const ms = msOf(pointerX(event));
                if (event.shiftKey) {
                  onMarked({ fromMs: ms, toMs: ms });
                  begin(event, { type: 'mark', anchorMs: ms });
                } else {
                  onSeek(ms);
                  begin(event, { type: 'scrub' });
                }
              }}
              sx={{ position: 'absolute', left: 0, right: 0, top: 0, height: ROW.ruler, cursor: 'pointer', borderBottom: '1px solid #1F2937' }}
            >
              {ticks.map((t) => (
                <Box key={t} sx={{ position: 'absolute', left: xOf(t * 1000), top: 0, height: '100%', borderLeft: '1px solid #374151', pl: 0.5, fontSize: 10, color: '#9CA3AF', lineHeight: '14px' }}>
                  {step < 1 ? formatMs(t * 1000) : formatMs(t * 1000).replace(/\.\d$/, '')}
                </Box>
              ))}
              {hasMark ? (
                <Box sx={{ position: 'absolute', left: xOf(marked.fromMs as number), width: Math.max(2, xOf((marked.toMs as number) - (marked.fromMs as number))), top: 12, height: 12, bgcolor: 'rgba(250,204,21,0.35)', borderLeft: '2px solid #FACC15', borderRight: '2px solid #FACC15' }}>
                  <Box onPointerDown={(event: React.PointerEvent) => begin(event, { type: 'markEdge', edge: 'from' })} sx={{ position: 'absolute', left: -6, top: -2, width: 10, height: 16, cursor: 'ew-resize' }} data-testid="mark-from-handle" />
                  <Box onPointerDown={(event: React.PointerEvent) => begin(event, { type: 'markEdge', edge: 'to' })} sx={{ position: 'absolute', right: -6, top: -2, width: 10, height: 16, cursor: 'ew-resize' }} data-testid="mark-to-handle" />
                </Box>
              ) : null}
            </Box>

            {/* Video: the kept stretches */}
            <Box sx={{ position: 'absolute', left: 0, right: 0, top: tops.video, height: ROW.video }}>
              {placements.map(({ segment, startMs, endMs }, index) => {
                const left = xOf(startMs);
                const width = Math.max(4, xOf(endMs) - left);
                const playWidth = Math.max(2, xOf(segmentPlayMs(segment)));
                const selected = isSelected('segment', segment.id) || (segment.id === 'all' && isSelected('segment'));
                const said = firstWordsIn(words, segment.inMs, segment.outMs, 7);
                const thumbs: Array<{ x: number; src: string | null }> = [];
                for (let x = 0; x < playWidth && thumbs.length < 40; x += THUMB_WIDTH) {
                  const outAt = startMs + (x / pps) * 1000;
                  thumbs.push({ x, src: nearestFrame(frames, segment.inMs + (outAt - startMs) * segment.speed) });
                }
                return (
                  <Box
                    key={segment.id}
                    data-testid={`clip-${index}`}
                    role="button"
                    aria-label={`Stretch ${index + 1}: ${formatMs(startMs)} to ${formatMs(endMs)}`}
                    onPointerDown={(event: React.PointerEvent) => {
                      onSelect({ kind: 'segment', id: segment.id });
                      onSeek(msOf(pointerX(event)));
                      begin(event, { type: 'reorder', id: segment.id, startX: pointerX(event), moved: false, overIndex: index });
                    }}
                    sx={{
                      position: 'absolute', left, width, top: 2, bottom: 2, borderRadius: '5px', overflow: 'hidden', cursor: 'pointer',
                      bgcolor: CLIP_COLOURS[index % CLIP_COLOURS.length],
                      boxShadow: selected ? '0 0 0 2px #FFFFFF, 0 0 0 4px #2563EB' : 'inset 0 0 0 1px rgba(0,0,0,0.35)', touchAction: 'none',
                    }}
                  >
                    {thumbs.map((thumb) => (
                      thumb.src ? <img key={thumb.x} src={thumb.src} alt="" draggable={false} style={{ position: 'absolute', left: thumb.x, top: 0, height: '100%', width: THUMB_WIDTH, objectFit: 'cover', opacity: 0.85, pointerEvents: 'none' }} /> : null
                    ))}
                    {segment.holdMs > 0 ? (
                      <Box sx={{ position: 'absolute', left: playWidth, right: 0, top: 0, bottom: 0, background: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.25) 0 4px, transparent 4px 9px)' }} title="Held frame" />
                    ) : null}
                    <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, px: 0.5, pb: 0.25, fontSize: 11, lineHeight: '14px', color: '#fff', textShadow: '0 1px 2px #000', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', bgcolor: 'rgba(0,0,0,0.35)' }}>
                      {index + 1}{segment.speed !== 1 ? ` · ${segment.speed}×` : ''}{said ? ` · “${said}…”` : ''}
                    </Box>
                    <Box sx={{ position: 'absolute', right: 4, top: 2, display: 'flex', gap: 0.25 }}>
                      {segment.muted ? <VolumeOffIcon sx={{ fontSize: 14 }} titleAccess="Muted" /> : null}
                      {segment.holdMs > 0 ? <PauseCircleIcon sx={{ fontSize: 14 }} titleAccess="Holds its last frame" /> : null}
                    </Box>
                    <Box
                      data-testid={`trim-in-${index}`}
                      onPointerDown={(event: React.PointerEvent) => { onSelect({ kind: 'segment', id: segment.id }); begin(event, { type: 'trim', id: segment.id, edge: 'in', startX: pointerX(event), inMs: segment.inMs, outMs: segment.outMs, speed: segment.speed, key: `trim-${segment.id}-${Date.now()}` }); }}
                      sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 8, cursor: 'ew-resize', bgcolor: selected ? 'rgba(255,255,255,0.55)' : 'transparent', '&:hover': { bgcolor: 'rgba(255,255,255,0.55)' } }}
                    />
                    <Box
                      data-testid={`trim-out-${index}`}
                      onPointerDown={(event: React.PointerEvent) => { onSelect({ kind: 'segment', id: segment.id }); begin(event, { type: 'trim', id: segment.id, edge: 'out', startX: pointerX(event), inMs: segment.inMs, outMs: segment.outMs, speed: segment.speed, key: `trim-${segment.id}-${Date.now()}` }); }}
                      sx={{ position: 'absolute', left: Math.max(0, playWidth - 8), top: 0, bottom: 0, width: 8, cursor: 'ew-resize', bgcolor: selected ? 'rgba(255,255,255,0.55)' : 'transparent', '&:hover': { bgcolor: 'rgba(255,255,255,0.55)' } }}
                    />
                  </Box>
                );
              })}
              {dropIndex !== null && placements[dropIndex] ? (
                <Box sx={{ position: 'absolute', left: xOf(placements[dropIndex].startMs) - 2, top: 0, bottom: 0, width: 3, bgcolor: '#FACC15' }} />
              ) : null}
              {hasMark ? (
                <Box sx={{ position: 'absolute', left: xOf(marked.fromMs as number), width: xOf((marked.toMs as number) - (marked.fromMs as number)), top: 0, bottom: 0, bgcolor: 'rgba(250,204,21,0.25)', pointerEvents: 'none', borderLeft: '2px solid #FACC15', borderRight: '2px solid #FACC15' }} data-testid="marked-band" />
              ) : null}
            </Box>

            {/* Text */}
            <Box sx={{ position: 'absolute', left: 0, right: 0, top: tops.text, height: ROW.text }}>
              {list.texts.map((text) => bar('text', text.id, text.fromMs, text.toMs, text.text || '(empty)', '#7C3AED', true))}
            </Box>
            {/* Pictures */}
            <Box sx={{ position: 'absolute', left: 0, right: 0, top: tops.overlay, height: ROW.overlay }}>
              {list.overlays.map((overlay) => bar('overlay', overlay.id, overlay.fromMs ?? 0, overlay.toMs ?? total, assetName(overlay.assetId), '#2563EB', true))}
            </Box>
            {/* Captions */}
            <Box
              sx={{ position: 'absolute', left: 0, right: 0, top: tops.captions, height: ROW.captions, cursor: list.captions ? 'pointer' : 'default' }}
              onPointerDown={(event: React.PointerEvent) => { if (list.captions) { event.stopPropagation(); onSelect({ kind: 'captions' }); } }}
              data-testid="track-captions"
            >
              {list.captions ? cues.map((cue, index) => (
                <Box key={index} sx={{ position: 'absolute', left: xOf(cue.startMs), width: Math.max(2, xOf(cue.endMs - cue.startMs) - 1), top: 3, bottom: 3, borderRadius: '2px', bgcolor: isSelected('captions') ? '#34D399' : '#0D9488' }} />
              )) : null}
            </Box>
            {/* Voice */}
            <Box sx={{ position: 'absolute', left: 0, right: 0, top: tops.voice, height: ROW.voice }}>
              {list.voiceovers.map((voiceover) => bar('voiceover', voiceover.id, voiceover.atMs, Math.min(total, voiceover.atMs + voiceLength(voiceover.id)), voiceover.source.kind === 'tts' ? `“${voiceover.source.text}”` : assetName(voiceover.source.assetId), '#D97706', false))}
            </Box>
            {/* Music */}
            <Box sx={{ position: 'absolute', left: 0, right: 0, top: tops.music, height: ROW.music }}>
              {list.music ? bar('music', 'music', list.music.startMs, total, assetName(list.music.assetId), '#059669', false) : null}
            </Box>

            {/* Playhead and hover */}
            <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: xOf(outputMs) - 1, width: 2, bgcolor: '#F43F5E', pointerEvents: 'none' }} data-testid="playhead" />
            {hover && !dragRef.current ? (
              <Box sx={{ position: 'absolute', top: ROW.ruler, left: clamp(hover.x - 60, 0, contentWidth - 124), width: 120, pointerEvents: 'none', zIndex: 2 }}>
                {hoverFrame ? <img src={hoverFrame} alt="" style={{ width: 120, display: 'block', borderRadius: 4, border: '1px solid #374151' }} /> : null}
                <Box sx={{ fontSize: 11, textAlign: 'center', bgcolor: 'rgba(0,0,0,0.75)', borderRadius: 1, mt: 0.25 }}>
                  {formatMs(hover.ms)}{hoverSource !== null ? ` · source ${formatMs(hoverSource)}` : ''}
                </Box>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Timeline;
